/**
 * US Citrus Nursery Quiz — UX Update v2 copy.
 *
 * The USCN adaptation of the seven "slippery quiz" changes specified for
 * Dr. Mani's Magic in dr-mm-files/UX-dmm-quiz-ux-update-v2-for-ai. Same
 * mechanics, US Citrus Nursery voice.
 *
 * House rules: 3rd to 5th grade reading level, no em-dashes, no exclamation
 * points, no emojis in these strings. The excitement comes from the words
 * and the motion, never the punctuation.
 *
 * The counter is honest: 120 is the real reading space of the USCN engine
 * (6 result profiles x 10 archetypes x 2 location variants).
 */

// ===== UX-1: Micro-celebration copy banks =====

/**
 * Per-question line banks for the 7 scored questions. Rotate within a bank,
 * never repeat a line in a session. Overflow into CITRUS_CELEBRATION_GENERIC.
 */
export const CITRUS_CELEBRATION_BANKS: Record<string, string[]> = {
  location: [
    'Good. Now we know where your tree will live.',
    'That one answer just sent a quarter of the grove home.',
    'Smart start. Location decides more than people think.',
  ],
  experience: [
    'Honest. That is exactly what your tree needs from you.',
    'However long you have grown, there is a tree that meets you there.',
    'No judgment here. Just better matches.',
  ],
  flavor: [
    'Now your taste buds are talking.',
    'That craving says more about you than you think.',
    'The grove just got smaller. In a good way.',
  ],
  usage: [
    'Now we know what harvest day looks like at your place.',
    'Every box you check sharpens your match.',
    'We can see the harvest already.',
  ],
  vibe: [
    'Your style is showing. We like it.',
    'Not the usual pick. That tells us plenty.',
    'The field is thinning fast now.',
  ],
  'bonus-features': [
    'Noted. Your match list just got pickier.',
    'Good call. Little things decide big matches.',
    'Almost there. One answer from your reading.',
  ],
  identity: [
    'That was the key. Hold still while we read you back.',
    'Done. Your reading is ready to pick.',
    'The proudest answers make the truest matches.',
  ],
};

/** Generic overflow bank, used when a question's own bank is exhausted. */
export const CITRUS_CELEBRATION_GENERIC: string[] = [
  'Good pick.',
  'We can work with that.',
  'That narrows the grove.',
  'Your reading is taking shape.',
];

/**
 * Pick a celebration line for a question, never repeating a line in a session.
 * Question bank first, generic bank as overflow. The caller records the
 * returned line in its used set.
 */
export function pickCitrusCelebrationLine(questionId: string, used: ReadonlySet<string>): string {
  const bank = CITRUS_CELEBRATION_BANKS[questionId] || [];
  const fresh = bank.find((line) => !used.has(line));
  if (fresh) return fresh;
  const generic = CITRUS_CELEBRATION_GENERIC.find((line) => !used.has(line));
  if (generic) return generic;
  return bank[0] || CITRUS_CELEBRATION_GENERIC[0];
}

// ===== UX-5: Reading counter schedule + narration =====

export interface CitrusCounterStep {
  /** Count on the table after this question is answered. */
  count: number;
  /** One-line narration shown under the counter. */
  narration: string;
}

/** The counter opens on this before any answer. */
export const CITRUS_COUNTER_START: CitrusCounterStep = {
  count: 120,
  narration: 'One hundred twenty readings walk in. One walks out with your name on it.',
};

/**
 * Count after each scored question. Every answered question ticks down and
 * the count lands on exactly 1 at the final answer (identity).
 */
export const CITRUS_COUNTER_SCHEDULE: Record<string, CitrusCounterStep> = {
  location: { count: 96, narration: 'Where your tree lives just retired two dozen readings.' },
  experience: { count: 78, narration: 'Your experience level sent another eighteen home.' },
  flavor: { count: 60, narration: 'Half the field is gone. Yours is still in.' },
  usage: { count: 42, narration: 'Harvest plans are narrowing the grove fast.' },
  vibe: { count: 24, narration: 'Your style just cut the field again.' },
  'bonus-features': { count: 9, narration: 'Now it is getting picky. Nine readings left.' },
  identity: { count: 1, narration: 'One reading left. It has your name on it.' },
};

/** Chip label for a given count ("120 readings on the table" ... "1 reading, locked"). */
export function citrusCounterLabel(count: number): string {
  if (count === 1) return '1 reading, locked';
  return `${count} readings on the table`;
}

// ===== UX-6: Pre-reveal build-up sequence =====

export interface CitrusBuildUpLine {
  line: string;
  /** Growth-mark stage (1 to 5) reached while this line shows. */
  stage: 1 | 2 | 3 | 4 | 5;
}

/**
 * Four lines fade in one at a time, about 1 second each, over a seed mark
 * growing stage by stage. Anticipation, not a loading screen.
 */
export const CITRUS_BUILD_UP_LINES: CitrusBuildUpLine[] = [
  { line: 'Reading your answers.', stage: 1 },
  { line: 'Checking them against more than 40 citrus varieties.', stage: 2 },
  { line: 'Your best matches, found.', stage: 4 },
  { line: 'And your citrus reading is ready.', stage: 5 },
];

// ===== UX-2: Because line templates =====

export interface CitrusBecauseTemplate {
  id: string;
  /** The clause after "Because": full line renders as `Because {clause}, {payoff}.` */
  clause: string;
  payoff: string;
}

/** Render a template as its full line. */
export function citrusBecauseLine(template: CitrusBecauseTemplate): string {
  return `Because ${template.clause}, ${template.payoff}.`;
}

/**
 * Hero stacking rule: on the #1 match, combine the two strongest matched
 * clauses into one sentence, keeping the primary payoff.
 */
export function citrusBecauseLineStacked(
  primary: CitrusBecauseTemplate,
  secondary: CitrusBecauseTemplate
): string {
  return `Because ${primary.clause} and ${secondary.clause}, ${primary.payoff}.`;
}

/**
 * One template per quiz tag. The logic engine decides whether a tag fired
 * (person picked it AND the tree actually satisfies it), in strength order.
 */
export const CITRUS_BECAUSE_BY_TAG: Record<string, CitrusBecauseTemplate> = {
  EdibleRind: {
    id: 'edible_rind',
    clause: 'you want to eat the whole fruit',
    payoff: 'this one you can eat peel and all',
  },
  Snack: {
    id: 'snack',
    clause: 'you want fruit you can peel and eat on the spot',
    payoff: 'this one is made for snacking',
  },
  Juice: {
    id: 'juice',
    clause: 'fresh juice is the plan',
    payoff: 'this one earns its spot at breakfast',
  },
  Cook: {
    id: 'cook',
    clause: 'you cook and zest',
    payoff: 'this one belongs in your kitchen',
  },
  Aromatic: {
    id: 'aromatic',
    clause: 'you want the blossoms and the smell',
    payoff: 'this one fills the room',
  },
  Sweet: {
    id: 'sweet',
    clause: 'you want fruit that tastes like candy',
    payoff: 'this one brings the sweetness',
  },
  Sour: {
    id: 'sour',
    clause: 'you asked for that bright sour punch',
    payoff: 'this one delivers it',
  },
  Balanced: {
    id: 'balanced',
    clause: 'you want sweet with a bite',
    payoff: 'this one walks that line',
  },
  Seedless: {
    id: 'seedless',
    clause: 'you said no seeds',
    payoff: 'this one keeps snack time clean',
  },
  'Indoor-Patio': {
    id: 'indoor_patio',
    clause: 'your tree will live in a pot',
    payoff: 'this one stays happy in a container',
  },
  'In-Ground': {
    id: 'in_ground',
    clause: 'you are planting in the ground',
    payoff: 'this one grows into a true yard tree',
  },
  Beginner: {
    id: 'beginner',
    clause: 'you are just getting started',
    payoff: 'this one is forgiving while you learn',
  },
  Advanced: {
    id: 'advanced',
    clause: 'you have done this before',
    payoff: 'this one rewards a practiced hand',
  },
  Classic: {
    id: 'classic',
    clause: 'you want the classics everyone loves',
    payoff: 'this one earned its name',
  },
  Exotic: {
    id: 'exotic',
    clause: 'you want something rare',
    payoff: 'your neighbors will not have this one',
  },
  Ornamental: {
    id: 'ornamental',
    clause: 'you want a tree that looks the part',
    payoff: 'this one earns compliments',
  },
  Healer: {
    id: 'healer',
    clause: 'you are in it for the wellness side',
    payoff: 'this one carries it',
  },
  Cultural: {
    id: 'cultural',
    clause: 'you wanted history in the branches',
    payoff: 'this one comes with a story',
  },
};

/**
 * Strength order for picking a card's primary trigger and rotating across
 * neighboring cards. Benefit-heavy triggers lead so the hero line sells.
 */
export const CITRUS_BECAUSE_PRIORITY: string[] = [
  'EdibleRind',
  'Snack',
  'Juice',
  'Cook',
  'Aromatic',
  'Sweet',
  'Sour',
  'Balanced',
  'Seedless',
  'Indoor-Patio',
  'In-Ground',
  'Beginner',
  'Advanced',
  'Classic',
  'Exotic',
  'Ornamental',
  'Healer',
  'Cultural',
];

/** Fallback (no strong signal): the card still gets one honest line. */
export const CITRUS_BECAUSE_FALLBACK: CitrusBecauseTemplate = {
  id: 'fallback',
  clause: 'of what you picked and how you grow',
  payoff: 'this one belongs on your list',
};
